import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { CustomCollapse } from 'BrokerTsLib/src/components'
import type {
  CalcMinAmountHouseholdRequest,
  InsuranceParamsPropertyDto
} from 'BrokerTsLib/src/dtos'
import {
  Insurer,
  RiskVariant,
  Selection,
  SelectionType
} from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useState } from 'react'

import { SwitchControl } from '@/components/SwitchControl'
import { HouseFloorSelector } from '@/containers/HouseFloorSelector'
import { useCalcRecommendedAmountHousehold } from '@/hooks/useWizard'
import { Calculate, QuestionMark } from '@/icons'

import { FormGroup } from '../../../components/FormGroup'
import { FormLabel } from '../../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../../components/FormSection'
import { FormSpacer } from '../../../components/FormSpacer'
import { InputGroup } from '../../../components/InputGroup'
import { RadioStack } from '../../../components/RadioStack'
import {
  useInsurers,
  useSelections
} from '../../../hooks'
import { CustomNumberInput } from '../../CustomNumberInput'

export function HouseholdForm() {
  const { isInsurerAvailable } = useInsurers(false, null)
  const { mutateAsync: calcRecommendedHouseholdAmount } = useCalcRecommendedAmountHousehold()

  const { data: dataOwner } = useSelections(SelectionType.Vlastnictvi)
  const { data: dataTypKonstrukce } = useSelections(SelectionType.TypKonstrukce)
  const { data: dataVybaveni } = useSelections(SelectionType.Vybaveni)
  const { data: dataStrechy } = useSelections(SelectionType.TypStrechy)

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    control
  } = methods

  const isContracted = !!getValues('generalParams.contractNumber')
  const selectedRealtyType = watch('property.data.realtyTypeId')
  const selectedType = watch('property.type')
  const householdRoofType = watch('property.data.household.roofType')
  const houseWithBasementValue = watch('property.data.hasBasement')
  const householdNumberOfFloorsValue = watch('property.data.household.floorsCount')

  const isOpenApartment = !!selectedRealtyType && (selectedRealtyType == Selection.Byt || selectedRealtyType == Selection.NajemniByt)
  const isOpenRealty = !!selectedType && selectedType != RiskVariant.Household
  const isOpenHouseholdRoofSedlova = householdRoofType == Selection.StrechaSedlova

  const [calcHouseholdAmountError, setCalcHouseholdAmountError] = useState('')

  const calcHouseholdAmount = () => {
    const req: CalcMinAmountHouseholdRequest = {
      livingArea: getValues('property.data.household.livingArea'),
      equipment: getValues('property.data.household.equipment'),
    }
    calcRecommendedHouseholdAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.household.amount', resp.data.amount!)
        setCalcHouseholdAmountError('')
      } else {
        setCalcHouseholdAmountError(resp.data.message)
      }
    })
  }

  return (
    <FormSection>
      <FormSectionHeader>
        <FormSectionTitle>Domácnost</FormSectionTitle>

        <FormSectionDescription>Trvale obývaná.</FormSectionDescription>
      </FormSectionHeader>

      <FormSectionBody>
        <FormGroup>
          <FormControl isInvalid={!!errors.property?.data?.household?.floor} isRequired>
            <FormLabel tooltip="Pro domácnost v přízemí, sklepním bytě nebo vyvýšeném přízemí uveďte hodnotu 0, jinak uveďte číslo patra. Pokud je domácnost ve více podlažích, uveďte to nejnižší. Hodnota může být vyžadována pojišťovnou pro určení rizika povodně.">
              Poschodí
            </FormLabel>

            <InputGroup htmlSize={8} maxW={32} placeholder="Např. 1" {...register('property.data.household.floor')} errors={errors.property?.data?.household?.floor} isDisabled={isContracted} />
          </FormControl>

          {(isInsurerAvailable(Insurer.Axa) || isInsurerAvailable(Insurer.GeneraliCeskaPojistovna)) && <CustomCollapse in={isOpenApartment}>
            <FormControl>
              <FormLabel tooltip="Zaškrtněte, pokud je byt v posledním poschodí bytového domu">
                Poslední patro
              </FormLabel>
              <SwitchControl>
                <Switch {...register('property.data.household.isApartmentOnLastFloor')} isChecked={watch('property.data.household.isApartmentOnLastFloor')} isDisabled={isContracted} />
                <FormLabel htmlFor={'property.data.household.isApartmentOnLastFloor'} variant="switch">
                  Je v posledním poschodí
                </FormLabel>
              </SwitchControl>
            </FormControl>
          </CustomCollapse>}
        </FormGroup>

        <FormControl isInvalid={!!errors.property?.data?.household?.livingArea} isRequired>
          <FormLabel tooltip="Celková započitatelná plocha bytu je podlahová plocha bytu včetně kuchyně, chodeb, WC, koupelny a schodiště. Do této plochy se nezapočítávají plochy balkonů, lodžií, teras, plochy ostatních příslušenství bytu a nebytových prostor.">
            Podlahová plocha
          </FormLabel>

          <CustomNumberInput w={48} defaultValue={getValues('property.data.household.livingArea')} onValueChange={(_, value) => setValue('property.data.household.livingArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
            placeholder: 'Např. 128'
          }} />

          {errors.property?.data?.household?.livingArea && <FormErrorMessage>
            {errors.property?.data?.household?.livingArea.message}
          </FormErrorMessage>}
        </FormControl>

        <FormControl>
          <FormLabel tooltip="Napište číslo bytové jednotky číslicí. Pro byt v rodinném domě uveďte číslici 1 nebo 2 nebo 3.">
            Číslo bytu
          </FormLabel>

          <InputGroup htmlSize={8} maxW={32} placeholder="Např. 1" {...register('property.data.household.apartmentNumber')} isDisabled={isContracted} />
        </FormControl>

        <FormControl>
          <FormLabel tooltip="Zadejte počet osob v domácnosti">
            Počet osob v domácnosti
          </FormLabel>

          <InputGroup htmlSize={3} placeholder="Např. 5" {...register('property.data.household.countOfPeople')} isDisabled={isContracted} />
        </FormControl>

        {isInsurerAvailable(Insurer.Pillow) && <>
          <CustomCollapse in={!isOpenRealty}>
            <FormSpacer />
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty}>
            <FormControl isInvalid={!!errors.property?.data?.household?.approvedYear}>
              <FormLabel tooltip="Pokud je stavba zkolaudována, uveďte rok kolaudace. Pokud koladudace nebyla nutná uveďte rok výstavby.">
                Rok kolaudace či výstavby
              </FormLabel>

              <InputGroup htmlSize={4} {...register('property.data.household.approvedYear')} errors={errors.property?.data?.household?.approvedYear} isDisabled={isContracted} />
            </FormControl>
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty}>
            <FormControl isInvalid={!!errors.property?.data?.household?.structuralType}>
              <FormLabel tooltip="Typ konstrukce">
                Typ konstrukce
              </FormLabel>

              <RadioGroup name={register('property.data.household.structuralType').name} value={watch('property.data.household.structuralType')?.toString()} onChange={val => setValue('property.data.household.structuralType', Number(val))} isDisabled={isContracted}>
                <RadioStack variant="stackedTile">
                  {dataTypKonstrukce?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                    {item.description}
                  </Radio>)}
                </RadioStack>
              </RadioGroup>

              {errors.property?.data?.household?.structuralType && <FormErrorMessage>
                {errors.property?.data?.household?.structuralType.message}
              </FormErrorMessage>}
            </FormControl>
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty && !isOpenApartment}>
            <FormSpacer />

            <Flex sx={{
              columnGap: 32,
              rowGap: 8,
              placeItems: 'start',
              flexWrap: 'wrap'
            }}>
              <Box sx={{
                display: 'grid',
                gap: 4
              }}>
                <FormControl isInvalid={!!errors.property?.data?.household?.floorsCount}>
                  <FormLabel tooltip="Počet nadzemních podlaží">
                    Počet nadzemních podlaží
                  </FormLabel>

                  <InputGroup placeholder="Např. 1" {...register('property.data.household.floorsCount')} errors={errors.property?.data?.household?.floorsCount} isDisabled={isContracted} />
                </FormControl>

                <FormControl isInvalid={!!errors.property?.data?.household?.roofType}>
                  <FormLabel tooltip="Typ střechy">
                    Typ střechy
                  </FormLabel>

                  <RadioGroup name={register('property.data.household.roofType').name} value={watch('property.data.household.roofType')?.toString()} onChange={val => setValue('property.data.household.roofType', Number(val))} isDisabled={isContracted}>
                    <RadioStack variant="stackedTile">
                      {dataStrechy?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                        {item.description}
                      </Radio>)}
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.household?.roofType && <FormErrorMessage>
                    {errors.property?.data?.household?.roofType.message}
                  </FormErrorMessage>}
                </FormControl>

                <CustomCollapse in={isOpenHouseholdRoofSedlova}>
                  <FormControl isInvalid={!!errors.property?.data?.household?.isRoofResidential}>
                    <FormLabel tooltip="Je podkroví obytné">
                      Je podkroví obytné
                    </FormLabel>

                    <RadioGroup value={watch('property.data.household.isRoofResidential') == true ? 'yes' : watch('property.data.household.isRoofResidential') == false ? 'no' : undefined} onChange={val => setValue('property.data.household.isRoofResidential', val === 'yes')} isDisabled={isContracted}>
                      <RadioStack variant="stackedTile">
                        <Radio value="yes">Ano</Radio>
                        <Radio value="no">Ne</Radio>
                      </RadioStack>
                    </RadioGroup>

                    {errors.property?.data?.household?.isRoofResidential && <FormErrorMessage>
                      {errors.property?.data?.household?.isRoofResidential.message}
                    </FormErrorMessage>}
                  </FormControl>
                </CustomCollapse>
              </Box>

              {!isContracted && <Box sx={{
                display: 'grid',
                gap: 4
              }}>
                {
                  /* <FormLabel>Obývaná podlaží</FormLabel> */
                }

                <HouseFloorSelector floors={householdNumberOfFloorsValue ? parseInt(householdNumberOfFloorsValue.toString()) : 0} basement={!!houseWithBasementValue} roof={watch('property.data.household.isRoofResidential') ?? false} showRoof={isOpenHouseholdRoofSedlova} onRoofChange={(selected: boolean) => {
                  setValue('property.data.household.isRoofResidential', selected)
                }} onFloorsChange={(floors: number) => setValue('property.data.household.floorsCount', floors)} onBasementChange={(isSelected: boolean) => {
                  // setValue(
                  //   'property.data.household.floor',
                  //   (householdNumberOfFloorsValue ?? 1) + (isSelected ? 1 : 0),
                  // )
                  setValue('property.data.hasBasement', isSelected)
                }} />
              </Box>}
            </Flex>
          </CustomCollapse>

          <CustomCollapse in={!isOpenRealty && isOpenApartment}>
            <FormSpacer />

            <FormControl isInvalid={!!errors.property?.data?.household?.owner}>
              <FormLabel>Vlastnictví</FormLabel>

              <RadioGroup value={watch('property.data.household.owner')?.toString()} onChange={x => setValue('property.data.household.owner', parseInt(x))} isDisabled={isContracted}>
                <RadioStack variant="stackedTile">
                  {dataOwner?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                    {item.description}
                  </Radio>)}
                </RadioStack>
              </RadioGroup>

              {errors.property?.data?.household?.owner && <FormErrorMessage>
                {errors.property?.data?.household?.owner.message}
              </FormErrorMessage>}
            </FormControl>
          </CustomCollapse>
        </>}

        <FormSpacer />

        <FormControl isInvalid={!!errors.property?.data?.household?.equipment} isRequired>
          <FormLabel tooltip={<>
            Základní - domácnost je převážně vybavena základním
            zařízením pořízeným např. v bazaru a oblečení a obuv
            jsou nakupovány převážně v asijských tržnicích.
            <br />
            Standardní - domácnost je převážně vybavena standardním
            zařízením a oblečením a obuví, které jsou běžně dostupné
            na našem trhu (sériová výroba - např. IKEA, Sconto,
            Baťa, Hervis).
            <br />
            Luxusní - domácnost je převážně vybavena luxusním
            zařízením nebo zařízením pořízeným na zakázku, oblečení
            i obuv jsou převážně značkové."
          </>}>
            Vybavení
          </FormLabel>

          <RadioGroup name={register('property.data.household.equipment').name} value={watch('property.data.household.equipment')?.toString()} onChange={val => setValue('property.data.household.equipment', Number(val))} isDisabled={isContracted}>
            <RadioStack variant="stackedTile">
              {dataVybaveni?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                {item.description}
              </Radio>)}
            </RadioStack>
          </RadioGroup>

          {
            errors.property?.data?.household?.equipment && <FormErrorMessage>
              {errors.property?.data?.household?.equipment.message}
            </FormErrorMessage>
          }
        </FormControl >

        <FormGroup sx={{
          alignItems: 'end'
        }}>
          <FormControl isInvalid={!!errors.property?.data?.household?.amount} isRequired>
            <FormLabel tooltip="Nábytek, elektronika a spotřebiče, koberce, oblečení, hračky, nádobí, sportovní potřeby, kosmetika…">
              Vybavení domácnosti
            </FormLabel>

            <CustomNumberInput w={48} defaultValue={getValues('property.data.household.amount')} onValueChange={(_, value) => setValue('property.data.household.amount', value)} isDisabled={isContracted} inputRightElement="Kč" inputProps={{
              placeholder: 'Např. 10 000 000'
            }} />

            {errors.property?.data?.household?.amount && <FormErrorMessage>
              {errors.property?.data?.household?.amount.message}
            </FormErrorMessage>}
          </FormControl>

          <Flex sx={{
            gap: 4,
            alignItems: 'center'
          }}>
            <Button variant="outline" isDisabled={isContracted} onClick={() => calcHouseholdAmount()}>
              <Calculate color="secondary.500" />
              Orientační poj. částka
            </Button>

            <Tooltip shouldWrapChildren label="Jedná se o orientační pojistnou částku vypočítanou dle obecného vzorce, se započítaným nárůstem cen stavebních prací a materiálů, za kterou neneseme odpovědnost">
              <IconButton variant="ghost" aria-label="">
                <QuestionMark />
              </IconButton>
            </Tooltip>

            {calcHouseholdAmountError && <Text variant={'error'}>{calcHouseholdAmountError}</Text>}
          </Flex>
        </FormGroup>
      </FormSectionBody >
    </FormSection >)
}
